/**
 * Normalise an axios error from the reports API into { status, message, errors, section }.
 *
 * The backend answers 422 with { detail: { message, errors: [...] } } for
 * definition problems, 400 with a string detail for SQL guard/execution
 * errors, and the usual FastAPI/pydantic array for request-shape errors. Blob
 * responses (exports) carry the same JSON inside a Blob.
 */
import { sectionOfError } from './definition';

async function readData(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return data;
}

export async function extractApiError(error) {
  const status = error?.response?.status ?? null;
  if (!error?.response) {
    return { status, message: 'Server unreachable. Check that the backend is running.', errors: [], section: null };
  }
  const data = await readData(error);
  const detail = data?.detail;
  let message = '';
  let errors = [];

  if (typeof detail === 'string') {
    message = detail;
  } else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    message = detail.message || 'Request failed';
    errors = Array.isArray(detail.errors) ? detail.errors : [];
  } else if (Array.isArray(detail)) {
    errors = detail.map((d) => {
      const loc = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
      return loc ? `${loc}: ${d.msg}` : d.msg;
    });
    message = 'Invalid request';
  } else if (typeof data?.message === 'string') {
    message = data.message;
  } else {
    message = error.message || 'Request failed';
  }

  const section = errors.map(sectionOfError).find(Boolean) || null;
  return { status, message, errors, section };
}

export function summarise(apiError) {
  if (!apiError) return '';
  if (apiError.errors?.length) return `${apiError.message}: ${apiError.errors[0]}${apiError.errors.length > 1 ? ` (+${apiError.errors.length - 1} more)` : ''}`;
  return apiError.message;
}
